import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, CalculatorIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, LayersIcon, LayoutGridIcon, TargetIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const BRAND = 'MATEMATIK KLAR™';
export const heroGuarantees = ['Øjeblikkelig digital adgang', 'Engangsbetaling', 'Intet abonnement', '7 dages tilfredshedsgaranti'];
export const stats = [{
  value: '240',
  label: 'Sider i de seks bind'
}, {
  value: '6',
  label: 'Digitale bind'
}, {
  value: '10',
  label: 'Klassetrin – fra 0. til 9. klasse'
}, {
  value: '5',
  label: 'Bonusser med i pakken'
}, {
  value: '3',
  label: 'Niveauer i opgavearkene'
}];
export const differenceStats = [{
  value: '0.–9. klasse',
  label: 'Fra indskoling til udskoling'
}, {
  value: '6 digitale bind',
  label: 'Fra tal og regning og geometri til algebra, funktioner og evaluering'
}, {
  value: 'PDF klar til print',
  label: 'Brug materialet digitalt eller print det til klassen'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Aftener og weekender forsvinder',
  text: 'Timevis foran skærmen med at planlægge næste uges matematiktimer – og arbejdet er stadig langt fra færdigt.'
}, {
  title: 'Endeløs jagt på opgaver',
  text: 'Opgaverne fra forskellige hjemmesider er for lette, for svære eller skal tilpasses, før de virker i klassen.'
}, {
  title: 'Store faglige forskelle i klassen',
  text: 'Nogle elever har brug for konkreter og støtte, andre er færdige på fem minutter – og timen skal fungere for alle.'
}, {
  title: 'Elever der hænger fast',
  text: 'Du kan se, at nogle elever mangler forståelsen af positionssystemet, brøker eller ligninger – men tiden til at lave nyt materiale mangler.'
}, {
  title: 'Evaluering og dokumentation tager tid',
  text: 'Fælles Mål, løbende evaluering, karakterkriterier og elevplaner – hvordan begrunder og dokumenterer du det hele?'
}, {
  title: 'Stress når timen begynder',
  text: 'Når tiden løber fra dig, bliver timen let til »regn opgave 1–20 i bogen« og improvisation i sidste øjeblik.'
}];
export const afterItems = ['Færdig årsplan for 0.–9. klasse', '12 færdige lektioner minut for minut', 'Materialer og lærerinstruks til hver lektion', 'Opgaveark i 3 niveauer', 'Facit til alle opgaver', 'Koblet til Fælles Mål for matematik', 'Problemløsning og hverdagsmatematik', 'Prøver, evalueringskriterier og færdige feedbackkommentarer', 'PDF-filer klar til print'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Aftenerne går med planlægning',
  text: 'Bruger du aftener og weekender på at planlægge næste uges matematiktimer?'
}, {
  icon: FolderSearchIcon,
  title: 'Du leder efter de rigtige opgaver',
  text: 'Leder du hele tiden på nettet efter opgaver, der passer til klassetrinnet og til Fælles Mål?'
}, {
  icon: UsersIcon,
  title: 'Store forskelle i klassen',
  text: 'Har du brug for opgaver, der virker både for elever, der skal have støtte, og for elever, der skal udfordres?'
}, {
  icon: TargetIcon,
  title: 'Nogle elever halter efter',
  text: 'Ser du elever, der mangler et solidt fundament i regning, men du har svært ved at finde tid til ekstra materiale?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Evaluering tager tid',
  text: 'Vil du gerne have færdige prøver, kriterier og feedback – og hjælp til at forberede eleverne til FP9?'
}, {
  icon: LayoutGridIcon,
  title: 'Du savner en klar struktur',
  text: 'Vil du gerne have alt samlet ét sted, så du hurtigt finder den rigtige lektion, når timen starter?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Bind 01',
  level: '0.–3. klasse',
  title: 'Tal og regning',
  tagline: 'Du ved præcis, hvad der skal ske i hver time – hele skoleåret.',
  intro: 'Første bind giver dig en færdig årsplan for 1., 2. og 3. klasse og tolv færdige lektioner, som du kan gennemføre med det samme.',
  items: ['Årsplan for 1., 2. og 3. klasse', '40 skoleuger koblet til Fælles Mål', '12 færdige lektioner minut for minut', 'Lektioner på 45 minutter', 'Materialer og lærerinstruks til hver lektion', 'Talforståelse 0–1000 og positionssystemet', 'Addition, subtraktion, tierovergang og tiervenner', 'Gangetabellen, division som deling og hovedregning', 'Opgaveark i 3 niveauer med facit og prøver'],
  outro: 'Færdige lektioner, så du går ind i klassen med en klar plan.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 02',
  level: '0.–3. klasse',
  title: 'Geometri, måling og statistik',
  tagline: 'Gør matematikken konkret og synlig.',
  intro: 'Andet bind tager eleverne fra figurer og former til måling, uret, penge og de første diagrammer.',
  items: ['Figurer og former', 'Symmetri', 'Længde, vægt, rumfang og tid', 'Uret og penge (kroner og øre)', 'Tabeller og søjlediagrammer', 'Problemløsning med konkreter', 'Opgaveark i 3 niveauer', 'Spil og facit'],
  outro: 'Praktisk matematik, som eleverne kan tage og føle på.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 03',
  level: '4.–6. klasse',
  title: 'Tal, brøker og procent',
  tagline: 'En solid talforståelse – også i det svære stof.',
  intro: 'Tredje bind viser eleverne, hvordan store tal, decimaltal, brøker og procent hænger sammen.',
  items: ['Store tal og positionssystemet', 'Decimaltal', 'Brøker', 'Procent', 'Sammenhængen mellem brøker, decimaltal og procent', 'Overslag og afrunding', 'Hverdagsøkonomi', 'Færdige lektioner, opgaveark i 3 niveauer og facit'],
  outro: 'Klare lektioner, der gør brøker og procent forståelige for alle.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 04',
  level: '4.–6. klasse',
  title: 'Algebra, geometri og statistik',
  tagline: 'Fra mønstre til ligninger, vinkler og sandsynlighed.',
  intro: 'Fjerde bind introducerer algebraisk tænkning og uddyber geometri, statistik og programmering på mellemtrinnet.',
  items: ['Mønstre og ligninger', 'Udtryk', 'Koordinatsystemet', 'Vinkler', 'Areal og omkreds', 'Gennemsnit, median og typetal', 'Sandsynlighed', 'Programmering og algoritmer', 'Færdige lektioner, opgaveark og facit'],
  outro: 'Praktisk hjælp til at undervise konkret i abstrakte begreber.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 05',
  level: '7.–9. klasse',
  title: 'Tal, algebra og funktioner',
  tagline: 'Kernen i udskolingens matematik – færdigplanlagt.',
  intro: 'Femte bind dækker tal, algebra og funktioner i udskolingen – med programmering og privatøkonomi.',
  items: ['Negative tal', 'Potenser og kvadratrødder', 'Procent og vækst', 'Ligninger og uligheder', 'Lineære funktioner', 'Programmering og algoritmisk tænkning', 'Privatøkonomi: budget, lån og rente', 'Opgaveark i 3 niveauer og facit'],
  outro: 'Færdige lektioner, der åbner algebra og funktioner for hele klassen.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 06',
  level: '7.–9. klasse',
  title: 'Geometri, statistik, evaluering og FP9',
  tagline: 'Se, hvad eleverne kan – og forbered dem til folkeskolens prøve.',
  intro: 'Sjette bind samler geometri og statistik med værktøjer til evaluering og forberedelse til folkeskolens prøve i matematik (FP9).',
  items: ['Pythagoras og ligedannethed', 'Areal og rumfang', 'Statistik og sandsynlighed', 'Løbende evaluering', 'Evalueringskriterier i 3 niveauer', 'Prøver A/B med facit', 'Forberedelse til FP9', '100 færdige feedbackkommentarer'],
  outro: 'Fuld støtte til en retfærdig evaluering og til at gøre eleverne klar.',
  note: 'Materialet er udviklet uafhængigt og er hverken udgivet eller godkendt af Børne- og Undervisningsministeriet.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitale bind i matematik',
  text: 'Et komplet undervisningssystem, der hjælper dig med at planlægge og gennemføre matematikundervisningen hele skoleåret. Hvert bind har 40 sider med færdige lektioner, opgaveark i 3 niveauer og facit.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Færdige lektioner minut for minut',
  text: 'Slut med det blanke papir. Klare lektionsplaner med tidsforløb, materialer og lærerinstruks, så du hurtigt kommer i gang.'
}, {
  icon: FileTextIcon,
  title: 'Opgaveark i 3 niveauer',
  text: 'Samme mål i tre niveauer – støtte, basis og udfordring – så alle elever kan arbejde med det samme stof.'
}, {
  icon: CalculatorIcon,
  title: 'Facit til alle opgaver',
  text: 'Facit til opgaveark, opgaver og prøver, så du sparer tid på rettearbejdet.'
}, {
  icon: LayersIcon,
  title: 'Koblet til Fælles Mål',
  text: 'Kompetenceområder og færdigheds- og vidensmål for 1.–3., 4.–6. og 7.–9. klasse – tydeligt koblet til lektionerne.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Evaluering',
  text: 'Evalueringskriterier, prøver A/B, forberedelse til FP9 og 100 færdige feedbackkommentarer.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplan',
  text: 'Hjælp til at strukturere hele skoleåret og vælge emner efter klassetrin og klasse.'
}];
// Real pages from Bind 1 are added later. While the list is empty, the carousel is hidden
// and InsidePages shows the mockup with the Bind 1 contents instead.
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [];
export const pageTypes = ['Færdige lektioner', 'Opgaveark i 3 niveauer', 'Facit', 'Evaluering'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Årsplan i matematik – ugeplaner for 0.–9. klasse',
  text: 'Færdige ugeplaner for hele skoleåret, så du altid ved, hvad der skal ske i næste uge.',
  items: ['Ugeplaner for 0.–9. klasse', '40 skoleuger', 'Koblet til Fælles Mål', 'Klar til print'],
  benefit: 'Hele året planlagt – før det overhovedet begynder.'
}, {
  title: '100 ekstra opgaveark i matematik – med facit',
  text: 'Ekstra træning til elever, der har brug for mere, til dem der bliver hurtigt færdige – eller som lektier.',
  items: ['100 opgaveark', 'Til alle klassetrin', 'I forskellige sværhedsgrader', 'Facit følger med'],
  benefit: 'Altid noget meningsfuldt at give eleverne.'
}, {
  title: 'Matematikspil – 40 spil uden forberedelse',
  text: 'Spil, der gør træning og repetition aktiv og sjov. Reglerne er skrevet klart og tydeligt.',
  items: ['40 spil', 'Ingen forberedelse', 'For hele klassen, i par og i grupper', 'Hovedregning og talforståelse'],
  benefit: 'Mere motivation – mindre terperi.'
}, {
  title: '5 minutters hovedregning – 180 opvarmninger til hele skoleåret',
  text: 'Korte hovedregningsopvarmninger til timens start: eleverne er i gang med matematik fra første minut.',
  items: ['180 opvarmninger', 'Rækker til hele skoleåret', '5 minutter ad gangen', 'Facit følger med'],
  benefit: 'Hver time starter roligt og fokuseret.'
}, {
  title: 'Vikarpakken – 12 færdige matematiktimer',
  text: 'Tolv færdige timer, som en vikar kan gennemføre uden forberedelse.',
  items: ['12 færdige timer', 'Instruks til vikaren', 'Opgaveark med facit', 'Klar til print'],
  benefit: 'Ingen spildte timer, når du er fraværende.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Åbn MATEMATIK KLAR™',
  text: 'Hent pakken og åbn det bind, der passer til dit klassetrin.'
}, {
  title: 'Vælg klasse og lektion',
  text: 'Find den lektion, det emne eller det opgaveark, der passer til din klasse.'
}, {
  title: 'Forbered dig på 5 minutter',
  text: 'Læs lærerinstruksen og find de materialer frem, du skal bruge.'
}, {
  title: 'Print og start timen',
  text: 'Print opgavearkene eller brug materialet digitalt – og gå ind til timen med en klar plan.'
}];
export const audienceItems = ['Du underviser i matematik i 0.–9. klasse', 'Du er klasselærer og har matematik på skemaet', 'Du er specialundervisningslærer og støtter elever i matematik', 'Du vil have en færdig årsplan koblet til Fælles Mål', 'Du underviser klasser med store faglige forskelle', 'Du vil have opgaveark i 3 niveauer med facit', 'Du vil have hjælp til evaluering og FP9', 'Du vil have dine aftener og weekender tilbage'];
export const timeValueTasks = ['At lede efter opgaver på nettet', 'At planlægge lektioner', 'At lave opgaveark i flere niveauer', 'At lave facit og prøver', 'At evaluere og give feedback'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitale bind – 240 sider',
  detail: 'Tal og regning, geometri og måling, brøker og procent, algebra, funktioner og evaluering'
}, {
  label: '5 bonusser uden ekstra betaling',
  detail: 'Årsplan, 100 ekstra opgaveark, Matematikspil, 5 minutters hovedregning og Vikarpakken'
}, {
  label: 'Til 0.–9. klasse',
  detail: 'Færdige lektioner, opgaveark i 3 niveauer, facit og evalueringskriterier'
}];
export const offerIncludes = ['Øjeblikkelig digital adgang', 'PDF-filer du kan gemme', 'Klar til print', 'Intet fysisk produkt sendes'];
export const PRICE = '199 kr';
export const PRICE_BEFORE = '399 kr';

// Only add authentic reviews of MATEMATIK KLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hvordan får jeg materialet?',
  a: 'MATEMATIK KLAR™ leveres digitalt. Efter købet får du adgang til PDF-filerne, som du kan hente og gemme. Du kan bruge materialet på computer, tablet eller mobil og printe de sider, du har brug for.'
}, {
  q: 'Følger materialet Fælles Mål?',
  a: 'Ja. Årsplanen og lektionerne er koblet til Fælles Mål for matematik – kompetenceområderne Matematiske kompetencer, Tal og algebra, Geometri og måling samt Statistik og sandsynlighed – for 1.–3., 4.–6. og 7.–9. klasse. MATEMATIK KLAR™ er udviklet uafhængigt og er hverken udgivet eller godkendt af Børne- og Undervisningsministeriet.'
}, {
  q: 'Hvilke klassetrin passer materialet til?',
  a: 'Materialet dækker 0.–9. klasse: Bind 1 og 2 er til 0.–3. klasse, Bind 3 og 4 til 4.–6. klasse og Bind 5 og 6 til 7.–9. klasse. Du vælger selv lektioner og opgaver efter din klasse.'
}, {
  q: 'Kan jeg virkelig forberede en lektion på 5 minutter?',
  a: 'MATEMATIK KLAR™ er lavet til at gøre forberedelsen hurtig. Du åbner et bind, vælger en færdig lektion og læser den igennem på cirka fem minutter. Tiden kan variere efter lektionen og behovet for differentiering.'
}, {
  q: 'Rækker materialet til hele skoleåret?',
  a: 'MATEMATIK KLAR™ indeholder en årsplan for 40 skoleuger og færdige lektioner og forløb i alle bind. Du vælger, kombinerer og tilpasser materialet efter din egen plan og dine elevers behov.'
}, {
  q: 'Er der opgaveark i forskellige niveauer?',
  a: 'Ja. Opgavearkene findes i 3 niveauer – støtte, basis og udfordring – så elever med forskellige behov kan arbejde mod det samme mål.'
}, {
  q: 'Følger der facit med?',
  a: 'Ja. Der er facit til opgaveark, opgaver og prøver, så du sparer tid på rettearbejdet.'
}, {
  q: 'Er evaluering inkluderet?',
  a: 'Ja. Bind 6 indeholder værktøjer til løbende evaluering, evalueringskriterier i 3 niveauer, prøver A/B med facit, forberedelse til folkeskolens prøve i matematik (FP9) og 100 færdige feedbackkommentarer.'
}, {
  q: 'Kan materialet bruges i specialundervisning og holddeling?',
  a: 'Opgavearkene i 3 niveauer og de klare mål gør det lettere at differentiere, både i klassen, ved holddeling og i specialundervisningen. Materialet erstatter hverken skolens egne procedurer eller samarbejdet med PPR.'
}, {
  q: 'Skal jeg printe det hele?',
  a: 'Nej. Du vælger selv, hvilke sider du printer, og du kan også bruge dele af materialet digitalt.'
}, {
  q: 'Er de fem bonusser inkluderet i prisen?',
  a: `Ja. Alle fem bonusser er med i pakken uden ekstra betaling – i alt ${PRICE}.`
}, {
  q: 'Er det et abonnement?',
  a: 'Nej. Du betaler én gang og får adgang til det digitale materiale. Ingen løbende betalinger.'
}, {
  q: 'Kan jeg bruge materialet på mobil eller tablet?',
  a: 'Ja. PDF-filerne kan åbnes på computer, tablet og mobil. Vi anbefaler computer eller tablet, når du skal se opgavearkene igennem før print.'
}, {
  q: 'Er der en garanti?',
  a: 'Ja. MATEMATIK KLAR™ har 7 dages tilfredshedsgaranti. Er du mod forventning utilfreds med materialet, kan du kontakte os og få pengene tilbage inden for garantiperioden efter betingelserne.'
}];
export const footerLinks = ['Handelsbetingelser', 'Privatlivspolitik', 'Returnering', 'Kontakt'];
